import { Container, Row, Col } from 'react-bootstrap';
import NoteBox from '../Layout/NoteBox';
import PageSectionTitle from '../Layout/PageSectionTitle';
import PageSubsectionTitle from '../Layout/PageSubsectionTitle';
import MultiDataDownloadTile from './MultiDataDownloadTile';
import DataDownloadTile from './DataDownloadTile';
import "./DataTextContent.css";

const DataTextContent = () => {
  return(
    <div id="data-text-content">
      <Container fluid>
        <section id="section1">
          <PageSectionTitle>
            Tracks
          </PageSectionTitle>
          <span>
            The estimands of interest, described in the next tab, all take the form of impacts on monthly spending for the average treated patient (i.e. overall, 
            and by year, subgroup, and practice). We offer two "tracks" to estimating these patient-level effects.
          </span>
          <ul>
            <li>
              <span className="bold-text">Track 1</span> is a patient-level track. The datasets in this track include about 300,000 patients from 500 practices 
              over 4 years. With approximately 1.2 million patient-year records per dataset, these files are large enough to be computationally infeasible for 
              some methods.
            </li>
            <li>
              For methods that can't handle patient-level sample sizes, we also offer <span className="bold-text">Track 2</span>, a practice-level track, in which 
              the patient-year-level datasets from Track 1 have been averaged up to the practice-year level. Specifically, each dataset in Track 2 was calculated 
              by taking means of patient covariates and outcomes from the corresponding dataset in Track 1. We have provided these files of averages to make dataset 
              download times quicker. However, Track 2 teams may also download the patient-level files from Track 1 if they wish to include other practice-level 
              summary statistics (besides the averages) in their analyses. Note that to estimate impacts on the average treated patient, the estimand of interest, 
              Track 2 teams will need to use weights based on the number of patients in each practice, which can vary considerably across practices. Track 2 will 
              ascertain what effect aggregation to the practice level has on estimation method performance.
            </li>
          </ul>
          <p>
            Each track consists of 200 realizations each of 17 data generating processes (DGPs). Note that each realization of the DGP creates a new sample of 
            practices, meaning that practice #1 in the first realization is not the same practice as practice #1 in the second realization. The former could be a 
            small, rural, high-spending practice, whereas the latter might be a large, urban, low-spending practice.
          </p>
          <p>
            Teams may submit entries for either track or both.
          </p>
        </section>
        <section id='section2'>
          <PageSectionTitle>
            How the datasets were generated
          </PageSectionTitle>
          <p>
            A dataset from an evaluation of a real intervention informs the DGP. We have kept the intervention generic for the purposes of this competition 
            to avoid inadvertently disclosing any sensitive information. Although the DGPs use summaries of this real dataset, covariates, outcomes, and treatment 
            assignment are all simulated: the observations in the simulated datasets do not correspond directly to observations in the real data at either the 
            patient or practice level. 
          </p>
          <p>
            The marginal and joint distributions of many of the covariates are constructed to mirror those in the real Medicare dataset and are based on summaries of 
            the real dataset such as empirical CDFs, relationships between covariates, and intra-class correlations. The datasets also contain constructed covariates 
            that do not have a counterpart in the real data, to give us fine-grained control over the degree of confounding. All covariates are held fixed at their 
            baseline values; importantly, this implies that the intervention does not affect any covariates. The marginal distribution of the outcome is calibrated to 
            mirror that of the real dataset. Both the treatment and response surfaces are constructed and do not necessarily correspond to features of the real data 
            (beyond the marginal distribution of the outcome).
          </p>
        </section>
        <section id="section3">
          <PageSectionTitle>
            How the datasets are structured
          </PageSectionTitle>
          <p>
            In our datasets, patients are clustered within primary care practices. Practices (not individual patients) decide whether to join the intervention. This 
            means all patients in a treated practice are treated and all patients in an untreated practice are untreated.
          </p>
          <p>
            The data have a longitudinal structure, with repeated annual observations of patients over time. Although patients enter and leave the sample throughout the 
            four-year observation period, all primary care practices are observed for all four years. The first two years are a baseline period during which no one 
            receives the intervention. Then, at the beginning of Year 3, treated practices begin receiving the intervention. They continue receiving the 
            intervention through the end of Year 4.
          </p>
        </section>
        <section id="section4">
          <PageSectionTitle>
            Data dictionary
          </PageSectionTitle>
          <PageSubsectionTitle>
            Track 1
          </PageSubsectionTitle>
          <p>
            In the datasets for the patient-level track, each observation represents a single patient observed during a single year. The pair 
            (<span className="var-text">id.patient, year</span>) serves as a unique identifier. The datasets contain the following variables:
          </p>
          <NoteBox>
            <ul>
              <li><span className="var-text">id.patient:</span> Patient identifier</li>
              <li><span className="var-text">id.practice:</span> Practice identifier</li>
              <li>
                <span className="var-text">Z:</span> Indicator for whether practice <span className="var-text">id.practice</span> is in the 
                treatment group (<span className="var-text">Z</span> = 1) or comparison group (<span className="var-text">Z</span> = 0).
              </li>
              <li><span className="var-text">year:</span> Observation year (ranging from 1 to 4).</li>
              <li>
                <span className="var-text">post:</span> Indicator for whether the intervention has begun for treated 
                practices. <span className="var-text">post</span> = 1 in Years 3 and 4, <span className="var-text">post</span> = 0 in Years 1 and 2.
              </li>
              <li>
                <span className="var-text">Y:</span> Outcome (monthly Medicare expenditures for patient <span className="var-text">id.patient</span>, 
                in <span className="var-text">year</span>).
              </li>
              <li><span className="var-text">X1, X2, …, X5:</span> Unordered categorical and binary practice-level covariates used to define subgroup SATT estimands.</li>
              <li><span className="var-text">X6, X7, X8, X9:</span> Additional practice-level covariates.</li>
              <li><span className="var-text">V1, V2, …, V5:</span> Continuous, unordered categorical, and binary patient-level covariates</li>
            </ul>
          </NoteBox>
          <PageSubsectionTitle id="track-2">
            Track 2
          </PageSubsectionTitle>
          <p>
            In the datasets for the practice-level track, each observation represents an unweighted average over all patients in a single practice during a 
            single year. The pair (<span className="var-text">id.practice, year</span>) serves as a unique identifier. The datasets in the practice-level
            track will not have an <span className="var-text">id.patient</span> column. The variable <span className="var-text">n.patients</span> gives the 
            number of patients in practice <span className="var-text">id.practice</span> in Year <span className="var-text">year</span>; it serves as the 
            weighting variable used to define the patient-level sample average treatment effects on the treated (SATT).
          </p>
        </section>
        <section id="section5">
          <PageSectionTitle>
            Download <span className="new-data-note-title"><b>NEW</b> data as of 4/4</span> 
          </PageSectionTitle>
          <div id="data-download-tiles">
            <Row>
              <Col>
              <MultiDataDownloadTile
                title="Track 1 Datasets"
                url={
                  [
                    "./data/track1a_20220404.zip", 
                    "./data/track1b_20220404.zip",
                    "./data/track1c_20220404.zip"
                  ]
                }
              />
              </Col>
              <Col>
              <DataDownloadTile
                title="Track 2 Datasets"
                url="./data/track2_20220404.zip"
              />
              </Col>
            </Row>
          </div>
          
        </section>
      </Container>
    </div>
  )
}

export default DataTextContent;
